<template>
    <div id="connections-page">
        <loader v-if="executingCommand || loading" :addBackground="true"></loader>
        <confirm
            id="confirmCommand"
            ref="confirmCommandPopup"
            @confirm="executeCommand"
            @cancel="cancelCommand"
            :disabled="!isCommandFormValid"
        >
            <template slot="title">
                <span>{{ commandTitle }}</span>
            </template>
            <template slot="body">
                <div>
                    <div class="mb-3">{{ commandDescription }}</div>

                    <div v-if="activeCommandFields.length">
                        <div
                            class="field form-group"
                            v-for="(field, index) in activeCommandFields"
                            :key="index"
                        >
                            <custom-field
                                :field="field"
                                v-model="commandParams[field.id]"
                            />
                        </div>
                    </div>
                </div>
            </template>
        </confirm>
        <sender-form
            ref="senderForm"
            :place_sender="activePlaceSender"
            :sender="activeSender"
            :senders="senders"
            @done="done"
        />
        <div class="container-fluid">
            <div class="box padding-large page-content">
                <div class="title-container">
                    <h1>{{ t('settings-connections-title') }}</h1>
                    <router-link
                     to="/connections/add" class="btn btn-success" v-if="isGlobalAdmin && my_senders.length > 0">
                        <Icon icon-class="ui-plus" size="2" />
                        {{ t('settings-connections-add_new') }}
                    </router-link>
                </div>
                <div style="width: 210px; max-width: 100%;" v-show="my_senders.length > 0">
                    <Dropdown 
                    :options="channelOptions"
                    v-model="selectedChannel"
                    :placeholder="t('settings-connections-channel_placeholder')"
                >
                    <template #trigger="{ option, label }">
                        <div style="display: flex; align-items: center; justify-content: center; width: 20px; height: 20px;">
                            <span v-if="option.value=='sms'">
                                <span class="mdi mdi-message-text-outline"></span>
                            </span>
                            <span v-if="option.value=='email'">
                                <span class="mdi mdi-email-outline"></span>
                            </span>
                            <span v-if="option.value=='whatsapp'">
                                <img src="/images/senders/channels/whatsapp.svg" width="16" height="16" />
                            </span>
                            <span v-if="option.value=='whatsapp-cloud'">
                                <img src="/images/senders/channels/whatsapp-cloud.png" width="20" height="20" />
                            </span>
                            <span v-if="option.value=='rcs'">
                                <Icon icon-class="ui-message-notification-square" size="3" />
                            </span>
                        </div>
                        <span class="ml-2">{{ label }}</span>
                    </template>
                    <template #option="{ option, index, label, selected, highlighted }">
                        <div style="display: flex; align-items: center; justify-content: center; width: 20px; height: 20px;">
                            <span v-if="option.value=='sms'">
                                <span class="mdi mdi-message-text-outline"></span>
                            </span>
                            <span v-if="option.value=='email'">
                                <span class="mdi mdi-email-outline"></span>
                            </span>
                            <span v-if="option.value=='whatsapp'">
                                <img src="/images/senders/channels/whatsapp.svg" width="16" height="16" />
                            </span>
                            <span v-if="option.value=='whatsapp-cloud'">
                                <img src="/images/senders/channels/whatsapp-cloud.png" width="20" height="20" />
                            </span>
                            <span v-if="option.value=='rcs'">
                                <Icon icon-class="ui-message-notification-square" size="3" />
                            </span>
                        </div>
                        <span class="ml-2">{{ label }}</span>
                    </template>
                    </Dropdown>
                </div>
                <div class="box box-secondary mt-4" v-show="my_senders.length > 0">
                    <h2>{{ t('settings-connections-my_connections') }}</h2>
                    <div class="row mt-4" v-if="selectedChannel == 'sms'">
                        <div class="col-12">
                            <div class="">
                                <table class="table table-secondary">
                                    <thead>
                                        <tr>
                                            <th></th>
                                            <th>{{ t('settings-connections-table-connector') }}</th>
                                            <th>{{ t('settings-connections-table-name') }}</th>
                                            <th>{{ t('settings-connections-table-monthly_value') }}</th>
                                            <th>{{ t('settings-connections-table-cost_per_message') }}</th>
                                            <th>{{ t('settings-connections-table-webhook') }}</th>
                                            <th class="text-right">{{ t('settings-connections-table-actions') }}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <sms-row 
                                            v-for="sender in smsSenders" 
                                            :key="sender.id"
                                            :sender="sender" 
                                            :is-global-admin="isGlobalAdmin"
                                            :saving-price="savingPriceId"
                                            :saving-credits-cost="savingCreditsCostId"
                                            @click="edit(sender)" 
                                            @execute-command="confirmCommand(sender, $event)"
                                            @set-as-default="setAsDefaultSender(sender)"
                                            @open-webhooks="openWebhooks(sender)"
                                            @update-price="updateSenderPrice"
                                            @update-credits-cost="updateCreditsCost"
                                        />
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                    <div class="row mt-4" v-if="selectedChannel == 'email'">
                        <div class="col-12">
                            <div class="">
                                <table class="table table-secondary">
                                    <thead>
                                        <tr>
                                            <th></th>
                                            <th>{{ t('settings-connections-table-connector') }}</th>
                                            <th>{{ t('settings-connections-table-name') }}</th>
                                            <th>{{ t('settings-connections-table-monthly_value') }}</th>
                                            <th>{{ t('settings-connections-table-cost_per_message') }}</th>
                                            <th>{{ t('settings-connections-table-webhook') }}</th>
                                            <th class="text-right">{{ t('settings-connections-table-actions') }}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <email-row 
                                            v-for="sender in emailSenders" 
                                            :key="sender.id"
                                            :sender="sender" 
                                            :is-global-admin="isGlobalAdmin"
                                            :saving-price="savingPriceId"
                                            :saving-credits-cost="savingCreditsCostId"
                                            @click="edit(sender)" 
                                            @execute-command="confirmCommand(sender, $event)"
                                            @set-as-default="setAsDefaultSender(sender)"
                                            @open-webhooks="openWebhooks(sender)"
                                            @update-price="updateSenderPrice"
                                            @update-credits-cost="updateCreditsCost"
                                        />
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                    <div class="row mt-4" v-if="selectedChannel == 'whatsapp-cloud'">
                        <div class="col-12">
                            <div class="">
                                <table class="table table-secondary">
                                    <thead>
                                        <tr>
                                            <th></th>
                                            <th>{{ t('settings-connections-table-connector') }}</th>
                                            <th>{{ t('settings-connections-table-name') }}</th>
                                            <th>{{ t('settings-connections-table-monthly_value') }}</th>
                                            <th>{{ t('settings-connections-table-cost_per_message') }}</th>
                                            <th>{{ t('settings-connections-table-webhook') }}</th>
                                            <th class="text-right">{{ t('settings-connections-table-actions') }}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <whatsapp-cloud-row 
                                            v-for="sender in wppCloudSenders" 
                                            :key="sender.id"
                                            :sender="sender" 
                                            :is-global-admin="isGlobalAdmin"
                                            :saving-price="savingPriceId"
                                            :saving-credits-cost="savingCreditsCostId"
                                            @click="edit(sender)" 
                                            @execute-command="confirmCommand(sender, $event)"
                                            @set-as-default="setAsDefaultSender(sender)"
                                            @open-webhooks="openWebhooks(sender)"
                                            @update-price="updateSenderPrice"
                                            @update-credits-cost="updateCreditsCost"
                                        />
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                    <div class="row mt-4" v-if="selectedChannel == 'rcs' && $module('rcs')">
                        <div class="col-12">
                            <div class="">
                                <table class="table table-secondary">
                                    <thead>
                                        <tr>
                                            <th></th>
                                            <th>{{ t('settings-connections-table-connector') }}</th>
                                            <th>{{ t('settings-connections-table-name') }}</th>
                                            <th>{{ t('settings-connections-table-monthly_value') }}</th>
                                            <th>{{ t('settings-connections-table-cost_per_message') }}</th>
                                            <th>{{ t('settings-connections-table-webhook') }}</th>
                                            <th class="text-right">{{ t('settings-connections-table-actions') }}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <rcs-row
                                            v-for="sender in rcsSenders"
                                            :key="sender.id"
                                            :sender="sender"
                                            :is-global-admin="isGlobalAdmin"
                                            :saving-price="savingPriceId"
                                            :saving-credits-cost="savingCreditsCostId"
                                            @click="edit(sender)"
                                            @execute-command="confirmCommand(sender, $event)"
                                            @set-as-default="setAsDefaultSender(sender)"
                                            @open-webhooks="openWebhooks(sender)"
                                            @update-price="updateSenderPrice"
                                            @update-credits-cost="updateCreditsCost"
                                        />
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                    <div class="row mt-2" v-if="selectedChannel == 'whatsapp'">
                        <div class="col-12">
                            <table class="table table-secondary ">
                                <thead>
                                    <tr>
                                        <th></th>
                                        <th>{{ t('settings-connections-table-connector') }}</th>
                                        <th>{{ t('settings-connections-table-name') }}</th>
                                        <th>{{ t('settings-connections-table-status') }}</th>
                                        <th>{{ t('settings-connections-table-phone_number') }}</th>
                                        <th>
                                            {{ t('settings-connections-table-webhook') }}
                                            <Icon icon-class="ui-message-question-square" class="ml-2" size="2" :tooltip="t('settings-connections-webhook_read_help')" />
                                        </th>
                                        <th>{{ t('settings-connections-table-monthly_value') }}</th>
                                        <th>{{ t('settings-connections-table-cost_per_message') }}</th>
                                        <th class="text-right">{{ t('settings-connections-table-actions') }}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <whatsapp-row 
                                    v-for="sender in wppSenders" 
                                    :key="sender.id"
                                    :sender="sender" 
                                    :is-global-admin="isGlobalAdmin"
                                    :saving-price="savingPriceId"
                                    :saving-credits-cost="savingCreditsCostId"
                                    @click="edit(sender)" 
                                    @execute-command="confirmCommand(sender, $event)"
                                    @set-as-default="setAsDefaultSender(sender)"
                                    @update-price="updateSenderPrice"
                                    @update-credits-cost="updateCreditsCost" />
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
                <div class="empty-state" v-show="my_senders.length == 0 && !loading">
                    <div class="col-12 col-md-6 col-lg-4 mx-auto ">
                        <div class="box box-elevated padding-large">
                            <div class="icons">
                                <img src="/images/connections/connections.png" height="24" />
                            </div>
                            <h2 class="title-2">{{ t('settings-connections-first_connection_title') }}</h2>
                            <p class="text-sm">{{ t('settings-connections-first_connection_description') }}</p>
                            <router-link v-if="isGlobalAdmin" to="/connections/add" class="btn btn-outline btn-full">
                                <Icon icon-class="ui-plus" size="2" />
                                {{ t('settings-connections-add_new') }}
                            </router-link>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
<style>
.global-admin #connections-page .page-content {
    min-height: calc(100vh - 90px);
}
</style>
<style scoped lang="scss">
.page-content {
    min-height: calc(100vh - 20px);
    display: flex;
    flex-direction: column;
}
form .field {
    margin-bottom: 20px;
}
.senders-logos {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
    grid-gap: 20px;
    button {
        border: 4px solid transparent;
        &:disabled {
            opacity: 0.5;
            cursor: not-allowed;
            &.selected {
                border: 4px solid var(--primary);
            }
        }
        &:not(:disabled) {
            cursor: pointer;
            &.selected,
            &:hover {
                border: 4px solid var(--primary);
            }
        }
    }
}
.top {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 40px;
    h2 {
        margin-bottom: 0;
    }
}
.empty-state {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
    width: 100%;
    flex-grow: 1;
    .box {
        width: 100%;
        max-width: 400px;
        text-align: center;
        display: flex;
        flex-direction: column;
        gap: 24px;
        p, h2 {
            margin-bottom: 0;
        }
    }
}
</style>
<script>
import keviApi from "../../keviApi";
import Modal from "../../components/modal/modal";
import SenderList from "./senderList";
import Import from "../../clients/import";
import Dropdown from "../../components/dropdown/dropdown.vue";
import SenderForm from "./edit/sender-form.vue";
import WhatsappRow from "./rows/whatsapp.vue";
import SmsRow from "./rows/sms.vue";
import RcsRow from "./rows/rcs.vue";
import EmailRow from "./rows/email.vue";
import WhatsappCloudRow from "./rows/whatsapp-cloud.vue";
import CustomField from "../../components/custom-fields/custom-field.vue";
export default {
    components: { Import, Modal, SenderList, SenderForm, WhatsappRow, SmsRow, RcsRow, EmailRow, WhatsappCloudRow, CustomField },
    data() {
        return {
            senders: [],
            my_senders: [],
            form_fields: [],
            selectedChannel: this.$route.query.tab || 'whatsapp',
            executingCommand: false,
            loading: true,
            savingPriceId: null,
            savingCreditsCostId: null,
            activeCommand: {
                sender: null,
                command: null,
            },
            commandParams: {},
            activePlaceSender: {
                id: null,
                places_id: null,
                senders_id: null,
                alias: null,
                active: null,
                default_sender: null,
                fields: null,
                created_at: null,
                updated_at: null,
                credits_cost: null,
                sender_price: null,
                properties: null,
            },
            activeSender: {
                id: null,
                places_id: null,
                senders_id: null,
                alias: null,
                active: null,
                default_sender: null,
                fields: null,
            },
            form: {
                id: null,
                alias: null,
                test: null,
                channel: null,
                sender_id: null,
                form_fields: {},
            },
        };
    },
    computed: {
        activeCommandDefinition() {
            const sender = this.activeCommand?.sender;
            const commandKey = this.activeCommand?.command;
            if (!sender || !commandKey || !sender.commands) return null;
            return (Object.values(sender.commands) || []).find((c) => c.command === commandKey) || null;
        },
        activeCommandFields() {
            return this.activeCommandDefinition?.fields || [];
        },
        isCommandFormValid() {
            // If no command selected, allow the confirm popup to be closed via cancel only.
            if (!this.activeCommand?.command) return false;
            const fields = this.activeCommandFields || [];
            for (const field of fields) {
                if (field?.required) {
                    const v = this.commandParams?.[field.id];
                    if (!v) return false;
                }
            }
            return true;
        },
        commandTitle() {
            const cmd = this.activeCommand?.command || "";
            const def = this.activeCommandDefinition;
            return this.$_(
                `notifications-senders-commands-${cmd}-title`,
                null,
                def?.name || cmd,
            );
        },
        commandDescription() {
            const cmd = this.activeCommand?.command || "";
            const def = this.activeCommandDefinition;
            return this.$_(
                `notifications-senders-commands-${cmd}-description`,
                null,
                def?.description || "",
            );
        },
        isGlobalAdmin() {
            return Boolean(this.$store?.state?.user?.global_admin);
        },
        isValid() {
            if (!this.form.channel || !this.form.sender_id || !this.form.test) {
                return false;
            }
            for (let f in this.form.form_fields) {
                if (!this.form.form_fields[f]) {
                    return false;
                }
            }
            return true;
        },
        emailSenders() {
            return this.my_senders.filter((s) => s.channel === "email");
        },
        smsSenders() {
            return this.my_senders.filter((s) => s.channel === "sms");
        },
        rcsSenders() {
            return this.my_senders.filter((s) => s.channel === "rcs");
        },
        wppSenders() {
            return this.my_senders.filter((s) => s.channel === "whatsapp");
        },
        wppCloudSenders() {
            return this.my_senders.filter(
                (s) => s.channel === "whatsapp-cloud",
            );
        },
        sender() {
            return this.senders.find((s) => s.id === this.form.sender_id);
        },
        senderOptions() {
            if (!this.form.channel) {
                return [];
            }
            return this.senders.filter((s) => s.channel === this.form.channel);
        },
        channelOptions() {
            const options = [
                { label: this.t('settings-connections-channels-whatsapp'), value: 'whatsapp' },
                { label: this.t('settings-connections-channels-sms'), value: 'sms' },
                { label: this.t('settings-connections-channels-email'), value: 'email' },
                { label: this.t('settings-connections-channels-whatsapp_cloud'), value: 'whatsapp-cloud' },
            ];
            if (this.$module('rcs')) {
                options.splice(2, 0, { label: this.t('settings-connections-channels-rcs'), value: 'rcs' });
            }
            return options;
        },
    },
    async mounted() {
        try {    
            this.loading = true;
            await this.loadSenders();
            this.loading = false;
            this.$root.$on('reload-senders', this.reloadSenders);
        } catch (error) {
            this.$store.dispatch("notification/error", this.$_('settings-connections-notifications-load_error', [error?.body?.error || this.$_('settings-connections-notifications-unknown_error')]));
            this.loading = false;
        }
    },
    beforeDestroy() {
        this.$root.$off('reload-senders', this.reloadSenders);
    },
    methods: {
        openWebhooks(sender) {
            if (!sender?.id) return;
            if (sender?.webhook_supported === false || sender?.webhook_status === 'unavailable') return;
            this.$router.push(`/connections/${sender.id}/webhooks`);
        },
        cancelCommand() {
            this.activeCommand = {
                sender: null,
                command: null,
            };
            this.commandParams = {};
        },
        confirmCommand(sender, command) {
            this.activeCommand = {
                sender: sender,
                command: command,
            };
            // Initialize params for dynamic command fields
            this.commandParams = {};
            const def = (Object.values(sender?.commands || []) || []).find(
                (c) => c.command === command,
            );
            const fields = def?.fields || [];
            fields.forEach((field) => {
                this.$set(this.commandParams, field.id, "");
            });
            this.$refs["confirmCommandPopup"].open();
        },
        executeCommand(sender, command) {
            this.executingCommand = true;
            var options = keviApi.getOptions(
                "senders/commands/" +
                    this.activeCommand.sender.id +
                    "/" +
                    this.activeCommand.command,
                "POST",
                {
                    params: JSON.parse(JSON.stringify(this.commandParams || {})),
                },
            );
            this.$http(options).then((result) => {
                const updatedSender = result?.body?.sender || null;
                if (updatedSender?.id) {
                    this.my_senders = this.my_senders.map((s) =>
                        s.id === updatedSender.id ? updatedSender : s,
                    );
                }
                const cmd = this.activeCommand.command;
                const fallback =
                    cmd === "sendTest"
                        ? this.$_('settings-connections-notifications-test_sent_success')
                        : this.$_('settings-connections-notifications-command_executed_success');
                this.$store.dispatch(
                    "notification/success",
                    this.$_(
                        `notifications-senders-commands-${cmd}-success`,
                        null,
                        fallback,
                    ),
                );
                this.activeCommand = {
                    sender: null,
                    command: null,
                };
                this.commandParams = {};
                this.reloadSenders();
            }).catch((error) => {
                const errBody = error?.body || {};
                const errCode = errBody.error;
                const params = errBody.params || [];
                const fallbackMessage = errBody.message || (typeof errCode === 'string' ? errCode : this.$_('settings-connections-notifications-unknown_error'));
                const message = errCode ? this.$_(`settings-connections-notifications-${errCode}`, params, fallbackMessage) : fallbackMessage;
                this.$store.dispatch("notification/error", message);
                this.activeCommand = {
                    sender: null,
                    command: null,
                };
                this.commandParams = {};
            }).finally(() => {
                this.executingCommand = false;
            });
        },
        setAsDefaultSender(sender) {
            this.my_senders = this.my_senders.map((s) => s.id === sender.id ? { ...s, default_sender: '1' } : s);
            var options = keviApi.getOptions("senders/set-as-default/" + sender.id, "POST");
            this.$http(options).then(() => {
                this.$store.dispatch("notification/success", this.$_('settings-connections-notifications-set_default_success'));
                this.reloadSenders();
            }).catch((error) => {
                this.$store.dispatch("notification/error", this.$_('settings-connections-notifications-set_default_error', [error?.body?.error || this.$_('settings-connections-notifications-unknown_error')]));
            });
        },
        save() {
            if (this.$refs.senderForm.checkValidity()) {
                this.saveSender();
                this.resetForm();
            } else {
                this.$refs.senderForm.reportValidity();
            }
        },
        edit(sender) {
            this.activePlaceSender = sender;
            this.activeSender = this.senders.find((s) => s.id === sender.senders_id);
            this.$refs.senderForm.open();
        },
        resetForm(channel = null) {
            if (channel) {
                this.$refs.senderForm.open();
            } else {
                this.$refs.senderForm.close();
            }
            this.activePlaceSender = {
                id: null,
                alias: null,
                default_sender: false,
                fields: {},
                credits_cost: null,
                sender_price: null,
            };
            this.activeSender = {
                id: null,
                alias: null,
                default_sender: false,
                fields: {},
            };
        },
        async loadSenders() {
            let options = keviApi.getOptions("/senders", "GET");
            const response = await this.$http(options);
            this.senders = response.body.available_integrations;
            this.my_senders = response.body.my_senders;
        },
        async reloadSenders() {
            this.loading = true;
            await this.loadSenders();
            this.loading = false;
        },
        async saveSender() {
            let options = keviApi.getOptions(
                "/senders/save",
                "POST",
                JSON.parse(JSON.stringify(this.form)),
            );
            try {
                await this.$http(options);
                this.loadSenders();
                this.$store.dispatch(
                    "notification/success",
                    "Disparador salvo, teste enviado",
                );
            } catch (err) {
                this.$store.dispatch(
                    "notification/warning",
                    "Erro: " + (err.body.test_error || "desconhecido"),
                );
            }
        },
        done() {
            this.$refs.senderForm.close();
            this.reloadSenders();
        },
        async updateSenderPrice(sender, newPrice) {
            this.savingPriceId = sender.id;
            try {
                const options = keviApi.getOptions(
                    `senders/${sender.id}/update-price`, "POST",
                    { sender_price: newPrice }
                );
                await this.$http(options);
                sender.sender_price = newPrice;
                this.$store.dispatch("notification/success",
                    this.$_("settings-connections-notifications-price_updated_success", null, "Valor mensal atualizado com sucesso")
                );
            } catch (err) {
                this.$store.dispatch("notification/error",
                    this.$_("settings-connections-notifications-price_update_error", null, "Erro ao atualizar valor mensal")
                );
            } finally {
                this.savingPriceId = null;
            }
        },
        async updateCreditsCost(sender, newCost) {
            this.savingCreditsCostId = sender.id;
            try {
                const options = keviApi.getOptions(
                    `senders/${sender.id}/update-credits-cost`, "POST",
                    { credits_cost: newCost }
                );
                await this.$http(options);
                sender.credits_cost = newCost;
                this.$store.dispatch("notification/success",
                    this.$_("settings-connections-notifications-credits_cost_updated_success", null, "Custo por disparo atualizado com sucesso")
                );
            } catch (err) {
                this.$store.dispatch("notification/error",
                    this.$_("settings-connections-notifications-credits_cost_update_error", null, "Erro ao atualizar custo por disparo")
                );
            } finally {
                this.savingCreditsCostId = null;
            }
        },
    },
    watch: {
        "form.sender_id": function () {
            const sender = this.senders.find(
                (s) => s.id == this.form.sender_id,
            );
            this.form.test = null;
            this.form_fields = sender.form_fields ? [...sender.form_fields] : [];
            this.form.form_fields = {};
            let editting = {};
            if (this.form.id) {
                editting = this.my_senders.find((s) => s.id === this.form.id);
                if (editting) {
                    editting = editting.fields;
                }
            }
            this.form_fields.forEach((field) => {
                this.$set(this.form.form_fields, field.field_name, editting[field.id] || "");
            });
        },
        "selectedChannel": function () {
            if (this.$route.query.tab !== this.selectedChannel) {
                this.$router.push({
                    query: {    
                        tab: this.selectedChannel,
                    },
                });
            }
        },
        "$route.query.tab": function (newTab) {
            if (newTab && this.selectedChannel !== newTab) {
                this.selectedChannel = newTab;
            }
        },
    },
};
</script>
